import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import MoonDisc from "@/components/MoonDisc";
import { useLang } from "@/context/LanguageContext";
import { LOCALES, upcomingSabbats, isFullMoonDay, isNewMoonDay, fmtDate } from "@/lib/moon";
import { SABBAT_INFO } from "@/lib/sabbats";
import { ChevronLeft, ChevronRight, Sparkles, CalendarDays } from "lucide-react";

export default function LunarCalendar() {
  const { lang, t } = useLang();
  const loc = LOCALES[lang] || "en-GB";
  const [offset, setOffset] = useState(0);

  const base = new Date();
  const viewDate = new Date(base.getFullYear(), base.getMonth() + offset, 1);
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const startWeekday = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayStr = base.toDateString();

  const weekdays = [...Array(7)].map((_, i) => new Intl.DateTimeFormat(loc, { weekday: "short" }).format(new Date(2024, 0, 1 + i)));
  const monthLabel = new Intl.DateTimeFormat(loc, { month: "long", year: "numeric" }).format(viewDate);

  const cells = useMemo(() => {
    const arr = Array(startWeekday).fill(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(year, month, d));
    return arr;
  }, [startWeekday, daysInMonth, year, month]);

  const sabbatByDay = useMemo(() => {
    const map = {};
    upcomingSabbats(new Date(year, 0, 1), 8).forEach((s) => {
      if (s.date.getFullYear() === year && s.date.getMonth() === month) map[s.date.getDate()] = s.key;
    });
    // also include sabbats already passed this year (upcomingSabbats only future); add full-year list
    return map;
  }, [year, month]);

  // Full-year sabbat markers for the displayed month
  const monthSabbats = useMemo(() => {
    const m = {};
    for (const s of [
      { key: "imbolc", m: 2, d: 1 }, { key: "ostara", m: 3, d: 20 }, { key: "beltane", m: 5, d: 1 },
      { key: "litha", m: 6, d: 21 }, { key: "lughnasadh", m: 8, d: 1 }, { key: "mabon", m: 9, d: 22 },
      { key: "samhain", m: 10, d: 31 }, { key: "yule", m: 12, d: 21 },
    ]) if (s.m - 1 === month) m[s.d] = s.key;
    return m;
  }, [month]);

  const sabbats = upcomingSabbats(new Date(), 8);
  const { data: events = [] } = useQuery({ queryKey: ["lunarEvents"], queryFn: async () => (await api.get("/events", { params: { limit: 200 } })).data });

  const eventsForSabbat = (date, key) => events
    .filter((e) => e.sabbat === key || Math.abs(new Date(e.start_date) - date) <= 14 * 86400000)
    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
    .slice(0, 3);

  return (
    <>
      <Seo title={t("lunar_title")} description={t("lunar_subtitle")} canonicalPath="/lune" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="mb-10 border-b border-amber-900/30 pb-5">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-2">swiss-pagan.ch</div>
          <h1 className="font-serif text-4xl sm:text-5xl uppercase tracking-tight text-amber-100">{t("lunar_title")}</h1>
          <p className="text-stone-400 max-w-3xl mt-4">{t("lunar_subtitle")}</p>
        </div>

        {/* Month grid */}
        <div className="bg-[#121418] border border-[#272A34] rounded-xl p-4 sm:p-6 mb-14" data-testid="lunar-grid">
          <div className="flex items-center justify-between mb-5">
            <button data-testid="cal-prev" onClick={() => setOffset((o) => o - 1)} className="p-2 rounded-md text-stone-300 hover:text-amber-400 hover:bg-amber-900/10"><ChevronLeft className="w-5 h-5" /></button>
            <div className="font-serif text-xl sm:text-2xl text-amber-100 capitalize" data-testid="cal-month">{monthLabel}</div>
            <button data-testid="cal-next" onClick={() => setOffset((o) => o + 1)} className="p-2 rounded-md text-stone-300 hover:text-amber-400 hover:bg-amber-900/10"><ChevronRight className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 sm:gap-2">
            {weekdays.map((w, i) => <div key={i} className="text-center font-mono text-[10px] sm:text-xs uppercase tracking-wider text-stone-500 pb-1">{w}</div>)}
            {cells.map((date, i) => {
              if (!date) return <div key={`e${i}`} />;
              const isToday = date.toDateString() === todayStr;
              const full = isFullMoonDay(date);
              const nw = isNewMoonDay(date);
              const sabKey = monthSabbats[date.getDate()];
              return (
                <div
                  key={i}
                  data-testid={`cal-day-${date.getDate()}`}
                  className={`relative flex flex-col items-center justify-start gap-1 rounded-lg border p-1.5 sm:p-2 min-h-[64px] sm:min-h-[84px] ${isToday ? "border-amber-500 bg-amber-950/20" : full ? "border-amber-700/50 bg-[#15130f]" : "border-[#22252D] bg-[#0F1115]"}`}
                >
                  <span className={`text-[11px] font-mono ${isToday ? "text-amber-300 font-bold" : "text-stone-500"}`}>{date.getDate()}</span>
                  <MoonDisc date={date} size={26} />
                  {full && <span className="text-[8px] font-mono uppercase text-amber-400/90 leading-none">{t("moon_full")}</span>}
                  {nw && !full && <span className="text-[8px] font-mono uppercase text-stone-500 leading-none">{t("moon_new")}</span>}
                  {sabKey && <span title={t(`sabbat_${sabKey}`)} className="absolute top-1 right-1"><Sparkles className="w-3 h-3 text-amber-500" /></span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Wheel of the Year */}
        <div className="mb-6">
          <h2 className="font-serif text-2xl sm:text-3xl text-stone-100 flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> {t("lunar_wheel")}</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-5" data-testid="sabbats-list">
          {sabbats.map((s) => {
            const info = SABBAT_INFO[s.key];
            const today = new Date(base.getFullYear(), base.getMonth(), base.getDate());
            const days = Math.round((s.date - today) / 86400000);
            const evs = eventsForSabbat(s.date, s.key);
            return (
              <div key={`${s.key}-${s.date.getFullYear()}`} data-testid={`sabbat-card-${s.key}`} className="bg-[#121418] border border-[#272A34] rounded-xl p-6 hover:border-amber-500/40 transition-colors">
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="font-serif text-2xl text-amber-100">{t(`sabbat_${s.key}`)}</h3>
                  <span className="font-mono text-xs text-amber-400 shrink-0">{days === 0 ? t("lunar_today") : `${t("sabbat_next").split(" ")[0]} · ${days} ${days === 1 ? t("sabbat_day") : t("sabbat_days")}`}</span>
                </div>
                <div className="font-mono text-xs text-stone-500 mb-3">{fmtDate(s.date, lang, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
                <p className="text-sm text-stone-400 leading-relaxed"><span className="text-amber-500 font-mono text-[11px] uppercase tracking-wider mr-1">{t("lunar_history")}:</span>{info.history[lang]}</p>
                <p className="text-sm text-stone-400 leading-relaxed mt-2"><span className="text-amber-500 font-mono text-[11px] uppercase tracking-wider mr-1">{t("lunar_ritual")}:</span>{info.ritual[lang]}</p>
                <div className="mt-4 pt-4 border-t border-[#22252D]">
                  <div className="font-mono text-[11px] uppercase tracking-wider text-stone-500 mb-2 flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> {t("lunar_events")}</div>
                  {evs.length === 0 ? (
                    <div className="text-sm text-stone-600">{t("lunar_no_events")}</div>
                  ) : (
                    <ul className="space-y-1.5">
                      {evs.map((e) => (
                        <li key={e.slug}>
                          <Link to={`/events/${e.slug}`} data-testid={`sabbat-event-${e.slug}`} className="text-sm text-stone-300 hover:text-amber-400 flex items-center justify-between gap-3">
                            <span className="truncate">{e.title}</span>
                            <span className="font-mono text-[11px] text-stone-500 shrink-0">{fmtDate(new Date(e.start_date), lang)}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
